/******************************************************************
 * Archivo : client/src/components/Admin/users/AllUsers.js
 * Proyecto: CB30
 * Módulo  : Administración de Usuarios
 *
 * Propósito:
 * Visualizar y administrar los usuarios registrados en el sistema.
 *
 * Funcionalidades:
 * - Buscar usuarios.
 * - Crear usuarios.
 * - Editar usuarios.
 * - Cambiar tipo de perfil.
 * - Activar / desactivar usuarios.
 * - Eliminar usuarios.
 * - Visualizar estado de validación.
 *
 * Compatible con:
 * React 16.x
 * Apollo 2.x
 ******************************************************************/

import React, { useState } from "react";
import { graphql, compose } from "react-apollo";
import { Link } from "react-router-dom";

// Icons
import FaSearch from "react-icons/lib/fa/search";
import FaPlus from "react-icons/lib/fa/plus";
import FaEdit from "react-icons/lib/fa/pencil";
import FaPowerOff from "react-icons/lib/fa/power-off";
import FaTrash from "react-icons/lib/fa/trash";

// Query
import query from "../../../queries/fetchAllUsers";

// Mutations
import deleteUser from "../../../mutations/deleteUser";
import activateUser from "../../../mutations/activateUser";
import profileType from "../../../mutations/profileType";

const AllUsers = props => {

  const [search, setSearch] = useState("");

  if (props.data.loading) {
    return (
      <div className="container">
        <p>Cargando usuarios...</p>
      </div>
    );
  }

  const users = props.data.users || [];

  const filteredUsers = users.filter(user => {

    const text = search.toLowerCase();

    return (
      (user.email || "").toLowerCase().includes(text) ||
      (user.nombre || "").toLowerCase().includes(text) ||
      (user.apellido || "").toLowerCase().includes(text)
    );
  });

  const handleProfileType = (id, value) => {

    props.profileType({
      variables: {
        id,
        profileType: parseInt(value, 10)
      }
    }).then(() => props.data.refetch());

  };

  const handleActivate = (id, active) => {

    props.activateUser({
      variables: {
        id,
        active: !active
      }
    }).then(() => props.data.refetch());

  };

  const handleDelete = id => {

    if (!window.confirm(
      "¿Está seguro de eliminar este usuario?"
    )) {
      return;
    }

    props.deleteUser({
      variables: {
        id
      }
    }).then(() => props.data.refetch());

  };

  return (
    <div className="container">

      {/* Encabezado */}

      <div className="row mb-4">

        <div className="col-md-8">

          <h2>Administración de Usuarios</h2>

          <p className="text-muted">
            Gestione los usuarios registrados dentro del sistema.
          </p>

        </div>

        <div className="col-md-4 text-right">

          <Link
            to="/admin/usuarios/nuevo"
            className="btn btn-primary"
          >
            <FaPlus />
            {" "}
            Nuevo Usuario
          </Link>

        </div>

      </div>

      {/* Buscador */}

      <div className="row mb-3">

        <div className="col-md-6">

          <div className="input-group">

            <div className="input-group-prepend">

              <span className="input-group-text">
                <FaSearch />
              </span>

            </div>

            <input
              type="text"
              className="form-control"
              placeholder="Buscar por email, nombre o apellido..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />

          </div>

        </div>

      </div>

      {/* Tabla */}

      <table className="table table-hover table-striped">

        <thead>

          <tr>

            <th>Email</th>
            <th>Nombre</th>
            <th>Apellido</th>
            <th>Validado</th>
            <th>Tipo</th>
            <th>Activo</th>

            <th width="120">
              Acciones
            </th>

          </tr>

        </thead>

        <tbody>

          {filteredUsers.length === 0 ? (

            <tr>

              <td
                colSpan="7"
                className="text-center text-muted"
              >
                No existen usuarios registrados.
              </td>

            </tr>

          ) : (

            filteredUsers.map(user => (

              <tr key={user.id}>

                {/* Email */}

                <td>

                  <Link
                    to={`/admin/usuario/${user.id}`}
                  >
                    <strong>
                      {user.email}
                    </strong>
                  </Link>

                </td>

                {/* Nombre */}

                <td>
                  {user.nombre}
                </td>

                {/* Apellido */}

                <td>
                  {user.apellido}
                </td>

                {/* Validación */}

                <td>

                  {user.validated ? (

                    <span className="badge badge-success">
                      Sí
                    </span>

                  ) : (

                    <span className="badge badge-secondary">
                      No
                    </span>

                  )}

                </td>

                {/* Tipo de perfil */}

                <td>

                  <select
                    className="form-control form-control-sm"
                    style={{ width: "60px" }}
                    value={user.tipoPerfil || ""}
                    onChange={e =>
                      handleProfileType(
                        user.id,
                        e.target.value
                      )
                    }
                  >

                    <option value="">
                      -
                    </option>

                    <option value="0">0</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>

                  </select>

                </td>

                {/* Activo */}

                <td>

                  <button
                    type="button"
                    className={
                      user.active
                        ? "btn btn-sm btn-outline-success"
                        : "btn btn-sm btn-outline-secondary"
                    }
                    title={
                      user.active
                        ? "Inactivar usuario"
                        : "Activar usuario"
                    }
                    onClick={() =>
                      handleActivate(
                        user.id,
                        user.active
                      )
                    }
                  >

                    <FaPowerOff />

                  </button>

                </td>

                {/* Acciones */}

                <td>

                  <Link
                    to={`/admin/usuario/${user.id}`}
                    className="btn btn-sm btn-outline-primary mr-2"
                    title="Editar"
                  >

                    <FaEdit />

                  </Link>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    title="Eliminar"
                    onClick={() =>
                      handleDelete(user.id)
                    }
                  >

                    <FaTrash />

                  </button>

                </td>

              </tr>

            ))

          )}

        </tbody>

      </table>

      <div className="text-muted">

        Total de usuarios:{" "}
        <strong>
          {filteredUsers.length}
        </strong>

      </div>

    </div>
  );
};

const AllUsersMutations = compose(

  graphql(deleteUser, {
    name: "deleteUser"
  }),

  graphql(profileType, {
    name: "profileType"
  }),

  graphql(activateUser, {
    name: "activateUser"
  })

);

export default AllUsersMutations(
  graphql(query)(AllUsers)
);


// WEBPACK FOOTER //
// ./src/components/Admin/users/AllUsers.js